import { memo, useRef } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import Router from 'next/router';
import api from 'api';
import { actions } from 'store';
import { ModalButtonWithForm } from 'components/common';
import { FormGroup, Text } from 'components/common/form';

import type { EditorHeaderStateProps } from './EditorHeader';

type Props = Pick<EditorHeaderStateProps, 'currentChapter' | 'previewType'>;

const EditorHeaderSlug = ({ currentChapter, previewType }: Props) => {
  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { course },
    theme
  } = useReduxState();

  const slugInputContainer = useRef<HTMLDivElement>(undefined);

  const handleModalVisible = () =>
    slugInputContainer.current?.querySelector('input').focus();

  const submit = async (values: typeof initialValues) => {
    await api.put(`/admin/courses/${course.id}/chapters/${currentChapter.id}`, {
      slug: values.slug.trim()
    });

    dispatch(
      actions.addNotice({
        text: messages.editEditChapterLinkSuccess,
        type: 'success'
      })
    );

    // redirect to url with new slug
    if (values.slug !== currentChapter.slug) {
      Router.push(
        '/admin/courses/[courseSlug]/edit/[chapterSlug]',
        `/admin/courses/${course.slug}/edit/${values.slug}`
      );
    }

    return true;
  };

  const validateSlug = async (value: string) => {
    if (!value) return messages.editChapterLinkEmpty;

    const slugRegex = /^[A-Za-z0-9-_]+$/;
    const valid = value.trim().match(slugRegex);
    if (!valid) return messages.settingsCourseLinkInvalid;

    try {
      await api.head(
        `/admin/courses/${course.id}/chapters?slug=${value}` +
          `&id=ne|${currentChapter.id}`
      );

      return messages.editChapterLinkInUse;
    } catch (err) {
      return undefined;
    }
  };

  const initialValues = { slug: currentChapter.slug };

  const slugButtonStyle: React.CSSProperties = {
    backgroundColor: 'transparent',
    color: theme.textColorLighter,
    fontSize: '1.2rem',
    fontWeight: 400,
    maxWidth: '100%',
    padding: 0,
    textAlign: 'left'
  };

  return (
    <div className="slug">
      {previewType ? (
        <div
          style={slugButtonStyle}
        >{`/courses/${course.slug}/${currentChapter.slug}`}</div>
      ) : (
        <ModalButtonWithForm
          buttonCategory="transparent"
          buttonSize="small"
          buttonStyle={slugButtonStyle}
          buttonText={
            <div
              aria-label={messages.editEditChapterLink}
              className="slug"
              data-tooltip={messages.editEditChapterLink}
            >
              <div className="slug-text">
                {`/courses/${course.slug}/${currentChapter.slug}`}
              </div>
            </div>
          }
          initialValues={initialValues}
          modalActionCategory="success"
          modalActionText={messages.commonSave}
          modalTitle={messages.editEditChapterLink}
          onModalVisible={handleModalVisible}
          onSubmit={submit}
        >
          {() => (
            <FormGroup htmlFor="slug" label={messages.editChapterLink}>
              <div className="link">
                <div className="link-url">{`/courses/${course.slug}/`}</div>
                <div className="link-input">
                  <Text
                    customValidateAsync={validateSlug}
                    field="slug"
                    id="slug"
                    inputContainerRef={(el: HTMLDivElement) =>
                      (slugInputContainer.current = el)
                    }
                  />
                </div>
              </div>
            </FormGroup>
          )}
        </ModalButtonWithForm>
      )}

      <style jsx>{`
        .slug {
          line-height: 1.2;
          white-space: normal;
          width: 100%;
        }
        .slug:focus,
        .slug:hover {
          text-decoration: underline;
        }
        .slug-text {
          display: -webkit-box;
          overflow: hidden;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 1;
        }
        .link {
          align-items: top;
          display: flex;
          position: relative;
        }
        .link-url {
          color: #888;
          font-size: 1.4rem;
          font-weight: 400;
          margin-right: 2px;
          padding-top: 3px;
        }
        .link-input {
          flex-grow: 1;
          min-width: 160px;
        }
        .link-input :global(input) {
          font-size: 1.4rem;
          padding: 2px 25px 2px 3px;
        }
        .link-input :global(.icon) {
          top: 5px;
          right: 7px;
        }
      `}</style>
    </div>
  );
};

export default memo(EditorHeaderSlug);
